/**
 * AuditTab — Phase 4 forensic timeline for the Central Admin.
 *
 * Read-only feed of every state-changing action on the platform.
 * Filters by action, role, date range, free-text. CSV export honours
 * the active filter set. No edit/delete affordances anywhere — audit
 * rows are append-only by design.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  adminListAuditLogs,
  adminAuditLogsMeta,
  adminAuditLogsCsvBlob,
} from "../../lib/api";
import { toast } from "sonner";
import {
  Search,
  Download,
  RotateCw,
  Filter,
  ShieldCheck,
  X,
} from "lucide-react";

// ── action → French label (kept in sync with audit.AUDIT_ACTIONS) ────
const ACTION_LABEL = {
  "admin.login": "Connexion admin",
  "admin.logout": "Déconnexion admin",
  "agency.created": "Agence créée",
  "agency.updated": "Agence modifiée",
  "agency.deleted": "Agence supprimée",
  "agency.activated": "Agence activée",
  "agency.deactivated": "Agence désactivée",
  "agency.password_reset": "Mot de passe Agence réinitialisé",
  "alert.review": "Vérification alerte",
  "alert.dispatched": "Alerte dispatchée",
  "alert.reassigned": "Alerte réaffectée",
  "alert.marked_false": "Alerte marquée fausse",
  "alert.status_changed": "Statut alerte modifié",
  "alert.reopened": "Alerte rouverte",
  "alert.acknowledged": "Alerte acquittée",
  "evidence.viewed": "Pièce jointe consultée",
  "evidence.case_file_opened": "Dossier opérationnel ouvert",
};
const ROLE_LABEL = {
  admin: "Central Admin",
  agency: "Agence",
  system: "Système",
};
const ROLE_COLOR = {
  admin: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  agency: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  system: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};
const ACTION_TONE = (action) => {
  if (action.startsWith("admin.login")) return "text-emerald-300";
  if (action.startsWith("agency.")) return "text-sky-300";
  if (action === "alert.dispatched") return "text-amber-300";
  if (action === "alert.reassigned") return "text-orange-300";
  if (action === "alert.marked_false") return "text-rose-300";
  if (action.startsWith("evidence.")) return "text-violet-300";
  return "text-zinc-200";
};

const DATE_FMT = new Intl.DateTimeFormat("fr-FR", {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
  hour12: false,
});

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return DATE_FMT.format(new Date(iso)); } catch { return iso; }
};

const DEFAULT_FILTERS = {
  action: "",
  actor_role: "",
  q: "",
  target_id: "",
  actor_email: "",
  since: "",
  until: "",
};

export const AuditTab = () => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState({ actions: [], total: 0 });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [expanded, setExpanded] = useState(() => new Set());
  const lastFetchRef = useRef(0);

  const queryParams = useMemo(() => {
    const p = { limit: 50 };
    for (const k of Object.keys(filters)) {
      const v = filters[k];
      if (v) {
        if ((k === "since" || k === "until") && v) {
          // datetime-local → ISO with explicit UTC
          try {
            p[k] = new Date(v).toISOString();
          } catch { /* ignore */ }
        } else {
          p[k] = v;
        }
      }
    }
    return p;
  }, [filters]);

  const refresh = async () => {
    const stamp = ++lastFetchRef.current;
    setLoading(true);
    try {
      const d = await adminListAuditLogs(queryParams);
      if (lastFetchRef.current !== stamp) return; // stale
      setItems(d.items || []);
      setCursor(d.next_cursor || null);
      setHasMore(!!d.has_more);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger l'audit");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await adminListAuditLogs({ ...queryParams, cursor });
      setItems((prev) => [...prev, ...(d.items || [])]);
      setCursor(d.next_cursor || null);
      setHasMore(!!d.has_more);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoadingMore(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const blob = await adminAuditLogsCsvBlob(queryParams);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'export");
    }
  };

  useEffect(() => {
    adminAuditLogsMeta().then(setMeta).catch(() => { /* noop */ });
  }, []);

  useEffect(() => { refresh(); }, [queryParams]);

  const applyFilters = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFilters(draft);
  };
  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters],
  );

  return (
    <div className="px-4 sm:px-6 py-5 space-y-5" data-testid="audit-tab">
      <div className="flex items-start gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-300 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Journal d&apos;audit
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Toutes les actions sensibles sur la plateforme sont enregistrées ici, en lecture seule.
            Les mots de passe ne sont jamais consignés.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span
            className="text-[10px] font-mono uppercase tracking-widest text-zinc-500"
            data-testid="audit-total-count"
          >
            {items.length} affichés · {meta.total ?? 0} au total
          </span>
          <button
            type="button"
            onClick={refresh}
            data-testid="audit-refresh"
            className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 rounded flex items-center gap-1.5"
          >
            <RotateCw className="w-3 h-3" /> Actualiser
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            data-testid="audit-export-csv"
            className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded flex items-center gap-1.5"
          >
            <Download className="w-3 h-3" /> Exporter CSV
          </button>
        </div>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────── */}
      <form
        onSubmit={applyFilters}
        className="bg-[#0b0f14] border border-zinc-800 rounded p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3"
        data-testid="audit-filter-form"
      >
        <label className="col-span-2 lg:col-span-2 flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          Recherche libre
          <div className="relative mt-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              type="text"
              value={draft.q}
              onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
              placeholder="Description, e-mail, libellé…"
              data-testid="audit-filter-q"
              className="w-full pl-7 pr-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 placeholder-zinc-600 focus:border-emerald-500/50 focus:outline-none"
            />
          </div>
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          Action
          <select
            value={draft.action}
            onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
            data-testid="audit-filter-action"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="">Toutes</option>
            {(meta.actions || []).map((a) => (
              <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          Rôle
          <select
            value={draft.actor_role}
            onChange={(e) => setDraft((d) => ({ ...d, actor_role: e.target.value }))}
            data-testid="audit-filter-role"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="">Tous</option>
            <option value="admin">Central Admin</option>
            <option value="agency">Agence</option>
            <option value="system">Système</option>
          </select>
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          ID cible (alerte/agence)
          <input
            type="text"
            value={draft.target_id}
            onChange={(e) => setDraft((d) => ({ ...d, target_id: e.target.value }))}
            placeholder="uuid…"
            data-testid="audit-filter-target"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 placeholder-zinc-600 focus:border-emerald-500/50 focus:outline-none"
          />
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          E-mail Agence
          <input
            type="text"
            value={draft.actor_email}
            onChange={(e) => setDraft((d) => ({ ...d, actor_email: e.target.value }))}
            placeholder="contient…"
            data-testid="audit-filter-email"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 placeholder-zinc-600 focus:border-emerald-500/50 focus:outline-none"
          />
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          Depuis
          <input
            type="datetime-local"
            value={draft.since}
            onChange={(e) => setDraft((d) => ({ ...d, since: e.target.value }))}
            data-testid="audit-filter-since"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
          />
        </label>
        <label className="flex flex-col text-[10px] uppercase tracking-widest text-zinc-500">
          Jusqu&apos;à
          <input
            type="datetime-local"
            value={draft.until}
            onChange={(e) => setDraft((d) => ({ ...d, until: e.target.value }))}
            data-testid="audit-filter-until"
            className="mt-1 px-2 py-1.5 bg-black/40 border border-zinc-800 rounded text-xs text-zinc-200 focus:border-emerald-500/50 focus:outline-none"
          />
        </label>
        <div className="col-span-1 md:col-span-2 lg:col-span-6 flex items-center gap-2 mt-1">
          <button
            type="submit"
            data-testid="audit-apply-filters"
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded flex items-center gap-1.5"
          >
            <Filter className="w-3 h-3" /> Appliquer
          </button>
          <button
            type="button"
            onClick={resetFilters}
            data-testid="audit-reset-filters"
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded flex items-center gap-1.5"
          >
            <X className="w-3 h-3" /> Réinitialiser
          </button>
          {activeFilterCount > 0 && (
            <span className="text-[10px] font-mono text-zinc-500 ml-1">
              {activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""} actif{activeFilterCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </form>

      {/* ── Timeline list ────────────────────────────────────────── */}
      {loading ? (
        <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest py-6">
          Chargement…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="audit-empty"
          className="text-xs text-zinc-500 font-mono uppercase tracking-widest py-6 border border-dashed border-zinc-800 rounded text-center"
        >
          Aucun événement pour ces filtres.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="audit-list">
          {items.map((it) => {
            const open = expanded.has(it.id);
            return (
              <li
                key={it.id}
                data-testid={`audit-row-${it.id}`}
                className="border border-zinc-800 rounded bg-[#0b0f14] hover:border-zinc-700 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(it.id)}
                  className="w-full text-left px-3 py-2.5 flex items-start gap-3"
                  data-testid={`audit-row-toggle-${it.id}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 shrink-0 w-36 pt-0.5">
                    {fmtTime(it.created_at)}
                  </span>
                  <span className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded border ${ROLE_COLOR[it.actor_role] || ROLE_COLOR.system}`}>
                    {ROLE_LABEL[it.actor_role] || it.actor_role}
                  </span>
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-widest ${ACTION_TONE(it.action)}`}>
                    {ACTION_LABEL[it.action] || it.action}
                  </span>
                  <span className="flex-1 text-xs text-zinc-200">
                    {it.description || "(sans description)"}
                  </span>
                  {it.actor_email && (
                    <span className="hidden md:inline text-[10px] text-zinc-500 font-mono">
                      {it.actor_email}
                    </span>
                  )}
                </button>
                {open && (
                  <div className="px-3 pb-3 pt-1 border-t border-zinc-800/60 text-[11px] text-zinc-400 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 font-mono">
                    <div><span className="text-zinc-600">ID&nbsp;:</span> {it.id}</div>
                    <div><span className="text-zinc-600">Acteur&nbsp;:</span> {it.actor_name || "—"} {it.actor_id ? `(${it.actor_id})` : ""}</div>
                    <div><span className="text-zinc-600">Cible&nbsp;:</span> {it.target_type || "—"} {it.target_id ? `· ${it.target_id}` : ""}</div>
                    <div><span className="text-zinc-600">Libellé cible&nbsp;:</span> {it.target_label || "—"}</div>
                    <div><span className="text-zinc-600">IP&nbsp;:</span> {it.ip || "—"}</div>
                    <div><span className="text-zinc-600">Pays&nbsp;:</span> {it.country_code || "—"}</div>
                    {it.details && Object.keys(it.details).length > 0 && (
                      <pre className="col-span-1 md:col-span-2 mt-1 p-2 bg-black/40 border border-zinc-800 rounded text-[10px] text-zinc-300 overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(it.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && !loading && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="audit-load-more"
            className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 rounded disabled:opacity-50"
          >
            {loadingMore ? "Chargement…" : "Charger plus"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditTab;
